import { useEffect, useRef } from "react";
import * as d3 from "d3";
import {
    DEFAULT_D3CIRCLE_CONSTRAINT,
  } from "../../../constants/commonConstants";

interface ClusterPreviewProps {
   speed?:number,
   charge?:number,
   // "page" (default) mirrors ensureRealtimeSimulation (the real live/
   // playback page-cluster simulation). "historical" mirrors the historical
   // dock's own simulation (clusterService.ts's drawBubbleNow and the other
   // two historical-cluster sites) - a DIFFERENT physics setup, notably an
   // extra "gravity" force pulling every bubble toward the shared dock
   // point (deliberate there - a real page cluster instead uses each
   // bubble's own per-page target, no gravity) and a weaker 0.05 x/y pull
   // instead of 0.3. Reusing the page-cluster forces for a historical
   // preview would show physics that don't match what historical spacing
   // actually looks like.
   variant?: "page" | "historical",
  }


// The ONE place the preview's forces are defined (see the long comment where they are used below for why
// they must mirror the real simulations). It is a function because the visible simulation and the offline
// probe that sizes the viewBox must run IDENTICAL physics, and two copies of a list of forces drift.
export const applyPreviewForces = (sim: any, charge: number | undefined, variant: "page" | "historical") => {
  sim
    .force("collision", d3.forceCollide().radius(5).strength(1))
    .force(
      "charge",
      d3.forceManyBody().strength(charge ? charge : DEFAULT_D3CIRCLE_CONSTRAINT.charge)
    )
    .force("x", d3.forceX(100).strength(variant === "historical" ? 0.05 : 0.3))
    .force("y", d3.forceY(70).strength(variant === "historical" ? 0.05 : 0.3));
  if (variant === "historical") {
    sim.force("gravity", d3.forceManyBody().strength(0.2));
  }
  return sim;
};

// The preview box is 200 x 140 of simulation space. A strong spacing setting spreads the historical cluster well
// past that (measured at rest: about x -18..232, y -35..188 at the strongest), so the preview would clip the very
// thing it exists to show. This settles the same physics offline (ten bubbles, a few hundred ticks, well under a
// millisecond of real work) and returns a viewBox that keeps the box at 1:1 whenever the cluster fits and zooms
// OUT, never in, only as far as it must when it does not.
const DEFAULT_VIEW = { x: 0, y: 0, w: 200, h: 140 };
export const fitViewBox = (initial: any[], charge: number | undefined, variant: "page" | "historical") => {
  const probe = applyPreviewForces(d3.forceSimulation(), charge, variant).stop();
  const nodes = initial.map((n) => ({ ...n }));
  probe.nodes(nodes);
  for (let i = 0; i < 400; i++) probe.tick();
  const pad = 8;
  const minX = Math.min(...nodes.map((n: any) => n.x - n.radius)) - pad;
  const maxX = Math.max(...nodes.map((n: any) => n.x + n.radius)) + pad;
  const minY = Math.min(...nodes.map((n: any) => n.y - n.radius)) - pad;
  const maxY = Math.max(...nodes.map((n: any) => n.y + n.radius)) + pad;
  const fits = minX >= 0 && maxX <= DEFAULT_VIEW.w && minY >= 0 && maxY <= DEFAULT_VIEW.h;
  if (!fits) {
    const scale = Math.max((maxX - minX) / DEFAULT_VIEW.w, (maxY - minY) / DEFAULT_VIEW.h);
    const w = DEFAULT_VIEW.w * scale, h = DEFAULT_VIEW.h * scale;
    return { x: (minX + maxX) / 2 - w / 2, y: (minY + maxY) / 2 - h / 2, w, h };
  }
  return DEFAULT_VIEW;
};

const ClusterPreview : React.FC<ClusterPreviewProps>  = ({charge, variant = "page"}) => {
  const svgRef = useRef(null);

  /**e
   * initial simulation hit
   */
  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const initialClusterRadius = 5; // Radius for the initial cluster
    let settingSimulation: any = null;
    const initialBubbles = [
      {
        id: 1,
        radius: 5,
        x: 150 - initialClusterRadius,
        y: 70,
      },
      {
        id: 2,
        radius: 5,
        x: 150 - initialClusterRadius / 2,
        y: 70,
      },
      {
        id: 3,
        radius: 5,
        x: 150,
        y: 70,
      },
      {
        id: 4,
        radius: 5,
        x: 150 + initialClusterRadius / 2,
        y: 70,
      },
      {
        id: 5,
        radius: 5,
        x: 150 + initialClusterRadius,
        y: 70,
      },
      {
        id: 6,
        radius: 5,
        x: 150 + initialClusterRadius * 2,
        y: 70,
      },
      {
        id: 7,
        radius: 5,
        x: 150,
        y: 70 + initialClusterRadius * 2,
      },
      {
        id: 8,
        radius: 5,
        x: 150 + initialClusterRadius * 1.5,
        y: 70,
      },
      {
        id: 9,
        radius: 5,
        x: 150,
        y: 70 + initialClusterRadius * 1.5,
      },
      {
        id: 10,
        radius: 5,
        x: 150 + initialClusterRadius * 2,
        y: 70 + initialClusterRadius * 2,
      },
    ];

    // Forces mirrored exactly from the real simulation this preview stands
    // in for - "page" mirrors clusterService.ts's ensureRealtimeSimulation
    // (the real live/playback page-cluster simulation), "historical"
    // mirrors drawBubbleNow/the other historical-cluster sites. This
    // preview previously ran its own, older "page" config that had drifted
    // out of sync: a "gravity" force (a second forceManyBody stacked on top
    // of "charge") that was deliberately REMOVED from the real page-cluster
    // simulation because it caused bubbles to visibly get tugged off-target
    // and never fully settle onto their cluster center ("shuttling" - see
    // ensureRealtimeSimulation's own comment for the full story), plus a
    // weaker x/y pull (0.14 vs the real 0.3) that made bubbles settle
    // looser than they actually do live. That same "gravity" force is
    // correct and intentional for the HISTORICAL dock, though - every
    // historical bubble pulls toward one shared dock point, not its own
    // per-page target, so the real historical simulation keeps it. Kept in
    // sync going forward: any change to either real simulation's forces
    // should be mirrored here too, since this preview's whole purpose is to
    // show what the real clustering will actually look like.
    settingSimulation = applyPreviewForces(d3.forceSimulation(), charge, variant);

    settingSimulation.on("tick", () => {
        // Direct attribute updates, no per-tick .transition() - the real
        // simulation's own tick handler doesn't transition either (see
        // ensureRealtimeSimulation), and chaining a new transition every
        // ~16ms with a duration far longer than the tick interval fought
        // itself and looked janky rather than smooth.
        svg
          .selectAll("circle")
          .attr("cx", (d: any) => d.x)
          .attr("cy", (d: any) => d.y);
      });

    settingSimulation.nodes(initialBubbles);

    const view = fitViewBox(initialBubbles, charge, variant);
    svg.attr("viewBox", `${view.x} ${view.y} ${view.w} ${view.h}`);

    svg
      .selectAll("circle")
      .data(initialBubbles)
      .enter()
      .append("circle")
      .attr("id", (d) => d.id)
      .attr("r", (d) => d.radius)
      .attr("fill", "steelblue")
      .attr("initial", true)
      .attr("cx", (d) => d.x)
      .attr("cy", (d) => d.y);

    return () => {
      settingSimulation.stop();
    };
  }, [charge, variant]);

  return (
    <>
      {/*
        * The simulation lives in a 200x200 space but only ever uses the band around y=70 (the pull
        * target) - the bottom 60px was dead space that made each preview twice as tall as its
        * contents. The viewBox crops it WITHOUT rescaling anything, so the spacing a preview shows is
        * still the real spacing; the box simply no longer reserves room nothing ever enters.
        * (The historical variant spreads furthest, about +-70 either side of y=70, which just fits.)
        */}
      <svg
        ref={svgRef}
        width={200}
        height={140}
        viewBox="0 0 200 140"
        style={{ display: "block", maxWidth: "100%", height: "auto" }}
      />
    </>
  );
};

export default ClusterPreview;
